import * as i0 from '@angular/core';
import { Inject, Injectable, NgModule } from '@angular/core';
import { DOCUMENT, CommonModule } from '@angular/common';
import { fromEvent, BehaviorSubject } from 'rxjs';
import { share, auditTime, map, filter, distinctUntilChanged, switchMap } from 'rxjs/operators';

var Size;
(function (Size) {
    Size["XS2"] = "2xs";
    Size["XS"] = "xs";
    Size["SM"] = "sm";
    Size["MD"] = "md";
    Size["LG"] = "lg";
    Size["XL"] = "xl";
    Size["XL2"] = "2xl";
    Size["XL3"] = "3xl";
    Size["XL4"] = "4xl";
})(Size || (Size = {}));

var ColorMode;
(function (ColorMode) {
    ColorMode["OnDark"] = "on-dark";
    ColorMode["OnLight"] = "on-light";
})(ColorMode || (ColorMode = {}));

var Variant;
(function (Variant) {
    Variant["Primary"] = "primary";
    Variant["Secondary"] = "secondary";
})(Variant || (Variant = {}));

var FontWeight;
(function (FontWeight) {
    FontWeight["Normal"] = "Normal";
    FontWeight["Bold"] = "Bold";
    FontWeight["Heavy"] = "Heavy";
})(FontWeight || (FontWeight = {}));

var IconPosition;
(function (IconPosition) {
    IconPosition["Leading"] = "leading";
    IconPosition["Trailing"] = "trailing";
    IconPosition["None"] = "";
})(IconPosition || (IconPosition = {}));

// @dynamic
class DocumentEventService {
    constructor(document) {
        this.events = {};
        this.document = document;
    }
    getEventObs(eventType) {
        if (!this.events[eventType]) {
            this.events[eventType] = fromEvent(this.document, eventType).pipe(share());
        }
        return this.events[eventType];
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DocumentEventService, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DocumentEventService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DocumentEventService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }] });

class FontReadyService {
    constructor(document) {
        this.document = document;
    }
    async fontReady(timeout = 1000) {
        const fontFaceSet = this.document.fonts;
        const fontFacePromise = fontFaceSet?.ready;
        const timeoutPromise = new Promise((resolve, reject) => {
            setTimeout(resolve, timeout);
        });
        return await Promise.race([fontFacePromise, timeoutPromise]);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FontReadyService, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FontReadyService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FontReadyService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }] });

class ResizeObserverService {
    constructor(zone) {
        this.zone = zone;
        this.entries = new BehaviorSubject([]);
        this.dbTime = 100;
        this.observer = new ResizeObserver(entries => {
            this.zone.run(() => {
                this.entries.next(entries);
            });
        });
    }
    observe(element) {
        this.observer.observe(element);
        return this.entries.pipe(auditTime(this.dbTime), map(entries => entries.filter(ent => ent.target === element)[0]?.contentRect), filter(entry => !!entry), distinctUntilChanged((a, b) => a.width === b.width && a.height === b.height));
    }
    unobserve(element) {
        this.observer.unobserve(element);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ResizeObserverService, deps: [{ token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ResizeObserverService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ResizeObserverService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i0.NgZone }] });

/**
 * Before using this transition observer, please make sure to:
 *  - Define the transition for your active state
 *  - Include '.c11n--transition-in' along with your active class for active styles
 *
 * Hidden class is optional in case your transition is one way.
 * (i.e. no other elements are required to disappear for active transition to happen)
 * If you wish your transition to be two way, make sure to add your own class or '.c11n-transition-obs--hidden'.
 */
class TransitionObserverService {
    constructor(rendererFactory) {
        this.activeClass = 'c11n-transition-obs--active';
        this.hiddenClass = '';
        this.activeIndex = new BehaviorSubject(0);
        this.previousIndex = -1;
        this.transitionInStarted = false;
        this.transitionInClass = 'c11n--transition-in';
        this.transitionOutClass = 'c11n--transition-out';
        this.subscriptions = [];
        this.renderer = rendererFactory.createRenderer(null, null);
    }
    unobserve() {
        this.previousIndex = -1;
        this.subscriptions.forEach((sub) => sub.unsubscribe());
        this.subscriptions = [];
        this.transitionOutSubscription = undefined;
        this.transitionInSubscription = undefined;
    }
    observe() {
        const observerSubscription = this.activeIndex
            .asObservable()
            .subscribe(() => {
            if ((this.transitionOutSubscription?.closed || !this.transitionOutSubscription) &&
                this.previousIndex !== -1) {
                // neither tranistionOut nor transitionIn started
                // start a new transition
                if (this.transitionInSubscription?.closed || !this.transitionInSubscription) {
                    this.transitionOutHandler();
                    this.renderer.addClass(this.widgetArray[this.previousIndex].nativeElement, this.transitionOutClass);
                    this.renderer.removeClass(this.widgetArray[this.previousIndex].nativeElement, this.activeClass);
                }
                else {
                    // transitionOut completed and transitionIn in progress
                    // cancel outdated transitionIn and start a new transition
                    // remember, outdated activeIndex now becomes previousIndex
                    if (this.transitionInStarted) {
                        this.transitionInSubscription?.unsubscribe();
                        this.transitionInStarted = false;
                        this.transitionOutHandler();
                        this.renderer.addClass(this.widgetArray[this.previousIndex].nativeElement, this.transitionOutClass);
                        this.renderer.removeClass(this.widgetArray[this.previousIndex].nativeElement, this.transitionInClass);
                    }
                    else {
                        // transitionOut completed and transitionIn not started
                        // start a new transitionIn with next activeIndex
                        // outdated activeIndex now becomes previousIndex and we only need to trigger transitionIn with next activeIndex
                        // as the opacity of outdated activeIndex never gets updated to 1 for transitionOut to happen if we remove hidden class
                        this.transitionInSubscription?.unsubscribe();
                        // Undo the prep that was done in transitionOut for the following transitionIn with outdated activeIndex
                        this.renderer.removeClass(this.widgetArray[this.previousIndex].nativeElement, this.transitionInClass);
                        if (this.hiddenClass !== '') {
                            this.renderer.addClass(this.widgetArray[this.previousIndex].nativeElement, this.hiddenClass);
                        }
                        // Do the prep that was done in transitionOut for the following transitionIn with next activeIndex
                        this.renderer.addClass(this.widgetArray[this.activeIndex.value].nativeElement, this.transitionInClass);
                        if (this.hiddenClass !== '') {
                            this.renderer.removeClass(this.widgetArray[this.activeIndex.value].nativeElement, this.hiddenClass);
                        }
                        this.transitionInHandler();
                    }
                }
            }
        });
        this.subscriptions.push(observerSubscription);
    }
    transitionInHandler() {
        this.transitionInSubscription = fromEvent(this.widgetArray[this.activeIndex.value].nativeElement, 'transitionstart').pipe(filter((response) => this.isResponseTargetEl(response)), switchMap((response) => {
            this.transitionInStarted = true;
            return fromEvent(response.target, 'transitionend').pipe(filter((response) => this.isResponseTargetEl(response)));
        }))
            .subscribe((response) => {
            this.transitionInSubscription?.unsubscribe();
            this.transitionInStarted = false;
            this.renderer.removeClass(response.target, this.transitionInClass);
            this.renderer.addClass(response.target, this.activeClass);
        });
        this.subscriptions.push(this.transitionInSubscription);
    }
    transitionOutHandler() {
        this.transitionOutSubscription = fromEvent(this.widgetArray[this.previousIndex].nativeElement, 'transitionstart').pipe(filter((response) => this.isResponseTargetEl(response)), switchMap((response) => {
            return fromEvent(response.target, 'transitionend').pipe(filter((response) => this.isResponseTargetEl(response)));
        }))
            .subscribe((response) => {
            this.transitionOutSubscription?.unsubscribe();
            this.renderer.removeClass(response.target, this.transitionOutClass);
            if (this.hiddenClass !== '') {
                this.renderer.addClass(response.target, this.hiddenClass);
                this.renderer.removeClass(this.widgetArray[this.activeIndex.value].nativeElement, this.hiddenClass);
            }
            this.renderer.addClass(this.widgetArray[this.activeIndex.value].nativeElement, this.transitionInClass);
            this.transitionInHandler();
        });
        this.subscriptions.push(this.transitionOutSubscription);
    }
    isResponseTargetEl(response) {
        const widgetElRefs = this.widgetArray.map((widget) => widget.nativeElement);
        return widgetElRefs.indexOf(response.target) != -1;
    }
    setIndex(index) {
        if (this.activeIndex.value !== index) {
            if (this.transitionOutSubscription?.closed || !this.transitionOutSubscription) {
                this.previousIndex = this.activeIndex.value;
            }
            this.activeIndex.next(index);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TransitionObserverService, deps: [{ token: i0.RendererFactory2 }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TransitionObserverService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TransitionObserverService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i0.RendererFactory2 }] });

class CoreModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CoreModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: CoreModule, imports: [CommonModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CoreModule, providers: [DocumentEventService, FontReadyService, ResizeObserverService, TransitionObserverService], imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CoreModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    providers: [DocumentEventService, FontReadyService, ResizeObserverService, TransitionObserverService]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ColorMode, CoreModule, DocumentEventService, FontReadyService, FontWeight, IconPosition, ResizeObserverService, Size, TransitionObserverService, Variant };
